import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Nullable } from 'global';
import { useEquipmentActions } from 'modules/equipment';
import { useInvoice } from 'modules/invoice';
import {
  dealBuilderApi,
  DealBuilderEntity,
  GetDealBuildersResponse,
  GetDealBuildersVariables,
  StartDealBuilderContactInput,
  StartDealBuilderResponse,
  StartDealBuilderVariables,
  UpsertDealBuilderContactsResponse,
  UpsertDealBuilderContactsVariables,
  UpsertDealBuilderEquipmentsResponse,
  UpsertDealBuilderEquipmentsVariables,
  UpdateDealBuilderQuoteResponse,
  UpdateDealBuilderQuoteVariables,
} from '../api';
import { QuoteDepositResponse } from '@trnsact/trnsact-shared-types';
import { CalculatedTotalsByLevel } from 'modules/quote';

type EquipmentUpdateDTO = Parameters<ReturnType<typeof useEquipmentActions>['onUpdateEquipment']>[0];
type InvoiceUpdateInput = Parameters<ReturnType<typeof useInvoice>['handleUpdateInvoice']>[0];

interface InitialDealBuilderData {
  ownerPgIds: string[];
  accountContactIds: string[];
  equipmentId: Nullable<string>;
  deposit: Nullable<QuoteDepositResponse>;
  calculatedTotals: CalculatedTotalsByLevel;
}

interface UseDealBuilderPersistenceResult {
  activeEntity: Nullable<DealBuilderEntity>;
  historyEntities: DealBuilderEntity[];
  loading: boolean;
  initialData: Nullable<InitialDealBuilderData>;
  setActiveEntity: (entity: DealBuilderEntity) => void;
  ensureDealBuilder: (contacts: StartDealBuilderContactInput[]) => Promise<Nullable<string>>;
  startNewQuote: () => Promise<Nullable<string>>;
  upsertContacts: (ownerPgIds: string[], accountContactIds: string[]) => void;
  upsertEquipments: (equipmentIds: string[], overrideProposalId?: string) => void;
  updateQuote: (input: Record<string, unknown>) => void;
  updateEquipments: (dtos: EquipmentUpdateDTO[]) => void;
  updateInvoice: (input: InvoiceUpdateInput) => void;
}

export const useDealBuilderPersistence = (
  vendorOpportunityId: Nullable<string>,
  activeProposalId: Nullable<string> = null
): UseDealBuilderPersistenceResult => {
  const [createdEntity, setCreatedEntity] = useState<Nullable<DealBuilderEntity>>(null);
  const startingRef = useRef(false);
  const { onUpdateEquipment } = useEquipmentActions();
  const { handleUpdateInvoice } = useInvoice();

  const {
    data: queryData,
    loading,
    refetch,
  } = useQuery<GetDealBuildersResponse, GetDealBuildersVariables>(dealBuilderApi.getDealBuilders, {
    variables: { vendorOpportunityId: vendorOpportunityId! },
    skip: !vendorOpportunityId,
  });

  const fetchedEntities = queryData?.dealBuilder.getDealBuilders.entity ?? [];
  const defaultEntity = fetchedEntities[0] ?? null;
  const overrideEntity = activeProposalId
    ? (fetchedEntities.find(e => e.proposalId === activeProposalId) ?? null)
    : null;
  const activeEntity = createdEntity ?? overrideEntity ?? defaultEntity;

  const historyEntities = useMemo(() => {
    if (!activeEntity) return [];
    return fetchedEntities.filter(e => e.proposalId !== activeEntity.proposalId);
  }, [fetchedEntities, activeEntity]);

  const initialData = useMemo((): Nullable<InitialDealBuilderData> => {
    if (!activeEntity) return null;
    const ownerPgIds = (activeEntity.proposalContacts ?? []).filter(c => c.ownerPgId).map(c => c.ownerPgId!);
    const accountContactIds = (activeEntity.proposalContacts ?? [])
      .filter(c => c.accountContactId)
      .map(c => c.accountContactId!);
    const equipmentId = activeEntity.proposalEquipments?.[0]?.equipmentId ?? null;
    const deposit = activeEntity.quote?.deposit ?? null;
    const calculatedTotals: CalculatedTotalsByLevel = {
      invoice: { fees: activeEntity.quote?.feesTotal ?? 0, taxes: 0, price: 0 },
      equipments: { fees: 0, taxes: 0, price: activeEntity.quote?.equipmentSubtotal ?? 0 },
      attachments: { fees: 0, taxes: 0, price: 0 },
    };
    return { ownerPgIds, accountContactIds, equipmentId, deposit, calculatedTotals };
  }, [activeEntity]);

  const [startDealBuilderMutation] = useMutation<StartDealBuilderResponse, StartDealBuilderVariables>(
    dealBuilderApi.startDealBuilder
  );

  const [upsertContactsMutation] = useMutation<UpsertDealBuilderContactsResponse, UpsertDealBuilderContactsVariables>(
    dealBuilderApi.upsertDealBuilderContacts
  );

  const [upsertEquipmentsMutation] = useMutation<
    UpsertDealBuilderEquipmentsResponse,
    UpsertDealBuilderEquipmentsVariables
  >(dealBuilderApi.upsertDealBuilderEquipments);

  const [updateDealBuilderQuoteMutation] = useMutation<UpdateDealBuilderQuoteResponse, UpdateDealBuilderQuoteVariables>(
    dealBuilderApi.updateDealBuilderQuote
  );

  const ensureDealBuilder = useCallback(
    async (contacts: StartDealBuilderContactInput[]): Promise<Nullable<string>> => {
      if (!vendorOpportunityId || activeEntity || startingRef.current) return null;
      startingRef.current = true;
      const { data } = await startDealBuilderMutation({
        variables: { input: { vendorOpportunityId, contacts } },
      });
      const entity = data?.dealBuilder.startDealBuilder.entity ?? null;
      if (entity) {
        setCreatedEntity(entity);
      }
      return entity?.proposalId ?? null;
    },
    [vendorOpportunityId, activeEntity, startDealBuilderMutation]
  );

  const startNewQuote = useCallback(async (): Promise<Nullable<string>> => {
    if (!vendorOpportunityId) return null;
    const { data } = await startDealBuilderMutation({
      variables: { input: { vendorOpportunityId } },
    });
    const entity = data?.dealBuilder.startDealBuilder.entity ?? null;
    if (entity) {
      setCreatedEntity(entity);
      refetch();
    }
    return entity?.proposalId ?? null;
  }, [vendorOpportunityId, startDealBuilderMutation, refetch]);

  const contactsTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const equipmentsTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const quoteTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const equipmentUpdateTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const invoiceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      clearTimeout(contactsTimerRef.current);
      clearTimeout(equipmentsTimerRef.current);
      clearTimeout(quoteTimerRef.current);
      clearTimeout(equipmentUpdateTimerRef.current);
      clearTimeout(invoiceTimerRef.current);
    };
  }, []);

  const upsertContacts = useCallback(
    (ownerPgIds: string[], accountContactIds: string[]) => {
      const proposalId = activeEntity?.proposalId;
      if (!proposalId) return;
      clearTimeout(contactsTimerRef.current);
      contactsTimerRef.current = setTimeout(() => {
        const contacts: StartDealBuilderContactInput[] = [
          ...ownerPgIds.map(id => ({ ownerPgId: id })),
          ...accountContactIds.map(id => ({ accountContactId: id })),
        ];
        upsertContactsMutation({ variables: { input: { proposalId, contacts } } });
      }, 500);
    },
    [activeEntity, upsertContactsMutation]
  );

  const upsertEquipments = useCallback(
    (equipmentIds: string[], overrideProposalId?: string) => {
      const proposalId = overrideProposalId ?? activeEntity?.proposalId;
      if (!proposalId) return;
      clearTimeout(equipmentsTimerRef.current);
      equipmentsTimerRef.current = setTimeout(() => {
        upsertEquipmentsMutation({ variables: { input: { proposalId, equipmentIds } } });
      }, 500);
    },
    [activeEntity, upsertEquipmentsMutation]
  );

  const updateQuote = useCallback(
    (input: Record<string, unknown>) => {
      const quoteId = activeEntity?.quote?.quoteId;
      if (!quoteId) return;
      clearTimeout(quoteTimerRef.current);
      quoteTimerRef.current = setTimeout(() => {
        updateDealBuilderQuoteMutation({ variables: { quoteId, input } });
      }, 1500);
    },
    [activeEntity, updateDealBuilderQuoteMutation]
  );

  const updateEquipments = useCallback(
    (dtos: EquipmentUpdateDTO[]) => {
      clearTimeout(equipmentUpdateTimerRef.current);
      equipmentUpdateTimerRef.current = setTimeout(() => {
        dtos.forEach(dto => onUpdateEquipment(dto, { withNotification: false }));
      }, 1500);
    },
    [onUpdateEquipment]
  );

  const updateInvoice = useCallback(
    (input: InvoiceUpdateInput) => {
      clearTimeout(invoiceTimerRef.current);
      invoiceTimerRef.current = setTimeout(() => {
        handleUpdateInvoice(input);
      }, 1500);
    },
    [handleUpdateInvoice]
  );

  return {
    activeEntity,
    historyEntities,
    loading,
    initialData,
    setActiveEntity: setCreatedEntity,
    ensureDealBuilder,
    startNewQuote,
    upsertContacts,
    upsertEquipments,
    updateQuote,
    updateEquipments,
    updateInvoice,
  };
};
